import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {RepeatMode} from '@/services/trackPlayer';
import {RateMode} from '@/services/trackPlayer/types';
import {IQueue, IQueueItem} from '@/types/common';
import {mapTrackIdToQueueItem} from '@/utils/player';

export interface PlayerState {
  queue: IQueue | null;
  isPlaying: boolean;
  isBuffering: boolean;
  repeatMode: RepeatMode;
  rateMode: RateMode;
  volume: number;
  dbSyncTimestamp: number;
  isActive: boolean;
}

export const initialState: PlayerState = {
  queue: null,
  isPlaying: false,
  isBuffering: false,
  repeatMode: RepeatMode.Off,
  rateMode: 1,
  volume: 1,
  dbSyncTimestamp: Date.now(),
  isActive: true,
};

export const playerSlice = createSlice({
  name: 'player',
  initialState,
  reducers: {
    updateSyncTime: state => {
      state.dbSyncTimestamp = Date.now();
    },
    setQueue: (state, action: PayloadAction<IQueue | null>) => {
      state.queue = action.payload;
    },
    reorderQueueTracks: (state, action: PayloadAction<IQueueItem[]>) => {
      if (!state.queue) {
        return;
      }

      if (state.queue.shuffledItems.length > 0) {
        state.queue.shuffledItems = action.payload;
        return;
      }

      state.queue.items = action.payload;
    },
    setCurrentTrackKey: (state, action: PayloadAction<string>) => {
      if (state.queue) {
        state.queue.currentTrackKey = action.payload;
      }
    },
    addTracksToQueue: (
      state,
      action: PayloadAction<{items: Pick<IQueueItem, 'trackId' | 'context'>[]}>,
    ) => {
      if (state.queue) {
        const items = action.payload.items.map(item =>
          mapTrackIdToQueueItem(item),
        );
        state.queue.items = [...state.queue.items, ...items];

        if (state.queue.shuffledItems.length > 0) {
          state.queue.shuffledItems = [...state.queue.shuffledItems, ...items];
        }
      }
    },
    removeTrackFromQueue: (
      state,
      action: PayloadAction<{trackKey: string}>,
    ) => {
      if (state.queue) {
        state.queue.items = state.queue.items.filter(
          item => item.key !== action.payload.trackKey,
        );

        if (state.queue.shuffledItems.length > 0) {
          state.queue.shuffledItems = state.queue.shuffledItems.filter(
            item => item.key !== action.payload.trackKey,
          );
        }
      }
    },
    setIsPlaying: (state, action: PayloadAction<boolean>) => {
      state.isPlaying = action.payload;
    },
    setIsBuffering: (state, action: PayloadAction<boolean>) => {
      state.isBuffering = action.payload;
    },
    setRepeatMode: (state, action: PayloadAction<RepeatMode>) => {
      state.repeatMode = action.payload;
    },
    setRateMode: (state, action: PayloadAction<RateMode>) => {
      state.rateMode = action.payload;
    },
    setVolume: (state, action: PayloadAction<number>) => {
      state.volume = action.payload;
    },
    setShuffleMode: (
      state,
      action: PayloadAction<{shuffledItems: IQueueItem[]}>,
    ) => {
      if (state.queue) {
        state.queue.shuffledItems = action.payload.shuffledItems;
      }
    },
    setIsActive: (state, action: PayloadAction<boolean>) => {
      state.isActive = action.payload;
    },
  },
});

export const {
  updateSyncTime,
  setQueue,
  reorderQueueTracks,
  addTracksToQueue,
  removeTrackFromQueue,
  setCurrentTrackKey,
  setIsPlaying,
  setIsBuffering,
  setRepeatMode,
  setRateMode,
  setVolume,
  setShuffleMode,
  setIsActive,
} = playerSlice.actions;

export default playerSlice.reducer;
